import {
  Component,
  signal,
  OnInit,
  OnDestroy,
  computed,
  inject,
  effect,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { trigger, style, animate, transition } from '@angular/animations';
import { rxResource } from '@angular/core/rxjs-interop';
import { PopupService } from '@millionshow/api-client';
import { Icon } from '@millionshow/client-libs';
import { SwiperDirective } from '../../directives/swiper.directive';
import type { SwiperOptions } from 'swiper/types';

@Component({
  selector: 'app-popup-banner',
  standalone: true,
  imports: [CommonModule, Icon, SwiperDirective],
  templateUrl: './popup-banner.component.html',
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'scale(0.9)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'scale(1)', opacity: 1 })),
      ]),
      transition(':leave', [
        animate(
          '200ms ease-in',
          style({ transform: 'scale(0.9)', opacity: 0 })
        ),
      ]),
    ]),
    trigger('slideUp', [
      transition(':enter', [
        style({ transform: 'translateY(100%)' }),
        animate('300ms ease-out', style({ transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateY(100%)' })),
      ]),
    ]),
  ],
})
export class PopupBannerComponent implements OnInit, OnDestroy {
  private readonly popupService = inject<PopupService>(PopupService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly storageKey = 'popup_dismissed';

  private dismissedPopups = signal<Set<string>>(new Set());

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  $popups = rxResource({
    stream: () =>
      this.popupService.popupControllerFindAllV1({ type: 'MILLIONSHOW' }),
  });

  visiblePopups = computed(() => {
    const popups = this.$popups.value() || [];
    const dismissed = this.dismissedPopups();
    return popups.filter(
      popup => !dismissed.has(popup.id) && !this.isHiddenForToday(popup.id)
    );
  });

  mobilePopupSwiperOptions = computed<SwiperOptions>(() => {
    const popupCount = this.visiblePopups().length;
    return {
      slidesPerView: 1,
      spaceBetween: 16,
      loop: popupCount > 1,
      autoplay:
        popupCount > 1
          ? {
              delay: 3000,
              disableOnInteraction: false,
            }
          : false,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
    };
  });

  private scrollLockEffect = effect(() => {
    if (!this.isBrowser) {
      return;
    }

    if (this.visiblePopups().length > 0) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  });

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }

    this.loadDismissedPopups();
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) {
      return;
    }

    document.body.style.overflow = '';
  }

  private loadDismissedPopups(): void {
    if (!this.isBrowser) {
      return;
    }

    const today = this.getTodayString();
    const dismissed = new Set<string>();
    const storedData = localStorage.getItem(this.storageKey);

    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.date === today) {
          parsed.ids.forEach((id: string) => dismissed.add(id));
        } else {
          localStorage.removeItem(this.storageKey);
        }
      } catch {
        localStorage.removeItem(this.storageKey);
      }
    }

    this.dismissedPopups.set(dismissed);
  }

  private isHiddenForToday(popupId: string): boolean {
    if (!this.isBrowser) {
      return false;
    }

    const today = this.getTodayString();
    const storedData = localStorage.getItem(this.storageKey);

    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.date === today) {
          return parsed.ids.includes(popupId);
        }
      } catch {
        return false;
      }
    }

    return false;
  }

  closePopup(popupId: string): void {
    const dismissed = new Set(this.dismissedPopups());
    dismissed.add(popupId);
    this.dismissedPopups.set(dismissed);
  }

  closeAllPopups(): void {
    const allPopupIds = this.visiblePopups().map(p => p.id);
    const dismissed = new Set(this.dismissedPopups());
    allPopupIds.forEach(id => dismissed.add(id));
    this.dismissedPopups.set(dismissed);
  }

  hideForToday(popupId: string): void {
    if (!this.isBrowser) {
      return;
    }

    const today = this.getTodayString();
    let storedData = { date: today, ids: [] as string[] };
    const existing = localStorage.getItem(this.storageKey);

    if (existing) {
      try {
        const parsed = JSON.parse(existing);
        if (parsed.date === today) {
          storedData = parsed;
        }
      } catch {
        // ignore parse errors
      }
    }

    if (!storedData.ids.includes(popupId)) {
      storedData.ids.push(popupId);
    }

    localStorage.setItem(this.storageKey, JSON.stringify(storedData));
    this.closePopup(popupId);
  }

  hideAllForToday(): void {
    const allPopupIds = this.visiblePopups().map(p => p.id);
    allPopupIds.forEach(id => this.hideForToday(id));
  }

  private getTodayString(): string {
    return new Date().toISOString().split('T')[0];
  }
}
